import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  notification,
} from 'antd'
import { Controller, useForm } from 'react-hook-form'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { debounce } from 'lodash'
import {
  accountsMounted,
  resetAccountsError,
  resetWalletsError,
  walletsMounted,
} from '../../balanceOperation/create/model'
import { useCreateBalanceOperation } from '../../balanceOperation/create/hooks'
import {
  $createError,
  $gateways,
  $isSuccessCreate,
  createDeposit,
  createDepositMounted,
  exchangerMounted,
  gatewaysListMounted,
  getGateways,
  resetExchangersError,
} from './model'
import { useUnit } from 'effector-react/effector-react.mjs'
import { useNavigate } from 'react-router-dom'
import { NotificationPlacement } from 'antd/es/notification/interface'
import { path } from '../../../../libs'

const TYPE_OPTIONS = [
  {
    label: 'Wallet',
    value: 'wallet',
  },
  {
    label: 'Account',
    value: 'account',
  },
  {
    label: 'Exchanger wallet',
    value: 'exchanger_wallet',
  },
]

type FormData = {
  clientId: string
  destinationType: string
  destinationId: string
  gatewayId: number | null
  initialAmount?: string
  clientAmount?: string
  comment: string
  internalComment: string
}

export const CreateDepositHeadwayForm = () => {
  const {
    accounts,
    accountsError,
    wallets,
    walletsError,
    exchangers,
    exchangersError,
    accountsLoading,
    walletsLoading,
    exchangersLoading,
  } = useCreateBalanceOperation()

  const isCreateDepositLoading = useUnit(createDeposit.pending)

  const [api, contextHolder] = notification.useNotification()

  const gateways = useUnit($gateways)
  const isGatewaysLoading = useUnit(getGateways.pending)
  const isSuccessCreate = useUnit($isSuccessCreate)
  const createError = useUnit($createError)

  const navigate = useNavigate()

  const openNotification = (
    placement: NotificationPlacement,
    type: string,
    message: string,
  ) => {
    api.error({
      message: type,
      description: message,
      placement,
    })
  }

  const { watch, handleSubmit, setError, clearErrors, control } =
    useForm<FormData>({
      mode: 'onChange',
      defaultValues: {
        clientId: '',
        destinationType: '',
        destinationId: '',
        gatewayId: null,
        initialAmount: '',
        clientAmount: '',
        comment: '',
        internalComment: '',
      },
    })

  const clientId = watch('clientId')
  const destinationType = watch('destinationType')
  const initialAmount = watch('initialAmount')
  const clientAmount = watch('clientAmount')

  const debounceLoadData = useRef(
    debounce((params: { id: string }, type: string) => {
      if (type === 'account') {
        accountsMounted(params)
      } else if (type === 'wallet') {
        walletsMounted(params)
      } else if (type === 'exchanger_wallet') {
        exchangerMounted({ clientId: params.id })
      }
    }, 500),
  )

  const debounceGatewaysSearch = useRef(
    debounce((searchTerm: string) => {
      if (searchTerm) {
        gatewaysListMounted({
          searchText: {
            internalNameAndId: searchTerm,
          },
        })
      }
    }, 1000),
  )

  const handleSearch = useCallback((id: string, type: string) => {
    resetAccountsError()
    resetWalletsError()
    resetExchangersError()
    if (id && type) {
      debounceLoadData.current({ id }, type)
    }
  }, [])

  useEffect(() => {
    return () => {
      debounceLoadData.current.cancel()
      debounceGatewaysSearch.current.cancel()
    }
  }, [])

  const isDisplayDestinationId = useMemo(
    () =>
      (destinationType === 'account' && accounts.length !== 0) ||
      (destinationType === 'wallet' && wallets.length !== 0) ||
      (destinationType === 'exchanger_wallet' && exchangers.length !== 0),
    [destinationType, accounts, wallets, exchangers],
  )

  const destinationOptions = useMemo(() => {
    if (destinationType === 'account') {
      return accounts
        .filter((account) => !account.isDemo)
        .map((account) => ({
          label: account.accountName,
          value: String(account.accountId),
        }))
    }
    if (destinationType === 'wallet') {
      return wallets.map((wallet) => ({
        label: wallet.currency,
        value: String(wallet.walletId),
      }))
    }
    if (destinationType === 'exchanger_wallet') {
      return exchangers.map((exchanger) => ({
        label: String(exchanger.id),
        value: String(exchanger.id),
      }))
    }
    return []
  }, [destinationType, accounts, wallets, exchangers])

  const onSubmit = (data: FormData) => {
    if (!data.gatewayId) {
      setError('gatewayId', {
        type: 'required',
        message: 'Gateway обязателен',
      })
      return
    }

    Modal.confirm({
      title: 'Are you ready?',
      onOk: () => {
        createDepositMounted({
          ...data,
          clientId: Number(data.clientId),
          gatewayId: Number(data.gatewayId),
          destinationId: Number(data.destinationId),
          initialAmount: Boolean(initialAmount) ? initialAmount : undefined,
          clientAmount: Boolean(clientAmount) ? clientAmount : undefined,
        })
      },
    })
  }

  useEffect(() => {
    if (!initialAmount || !clientAmount) {
      clearErrors('initialAmount')
      clearErrors('clientAmount')
    }
  }, [initialAmount, clientAmount])

  useEffect(() => {
    if (isSuccessCreate) {
      navigate(path.billing.balanceOperation.entry())

      openNotification('topRight', 'Success', 'Payment successfully created')
    }
  }, [isSuccessCreate])

  useEffect(() => {
    if (createError) {
      openNotification(
        'topRight',
        'Please contact the development team with this information',
        `${createError.code}: ${createError.message}`,
      )
    }
  }, [createError])

  return (
    <div className="pt-3">
      {contextHolder}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row gutter={16} className="mb-3">
          <Col span={8}>
            <Controller
              name="clientId"
              control={control}
              rules={{ required: 'Client ID обязателен' }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Client ID"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                >
                  <Input
                    {...field}
                    type="number"
                    onChange={(e) => {
                      field.onChange(e)
                      if (e.target.value) {
                        handleSearch(e.target.value, destinationType)
                      }
                    }}
                  />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={8}>
            <Controller
              name="destinationType"
              control={control}
              rules={{ required: 'DestinationType обязателен' }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Destination Type"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                >
                  <Select
                    {...field}
                    placeholder="Choose destination type"
                    style={{ width: '100%' }}
                    options={TYPE_OPTIONS}
                    onChange={(value) => {
                      field.onChange(value)
                      if (clientId && value) {
                        handleSearch(clientId, value)
                      }
                    }}
                  />
                </Form.Item>
              )}
            />
          </Col>

          {isDisplayDestinationId ? (
            <Col span={8}>
              <Controller
                name="destinationId"
                control={control}
                rules={{ required: 'DestinationId обязателен' }}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item
                    label="Destination ID"
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message}
                  >
                    <Select
                      {...field}
                      placeholder="Choose destination id"
                      disabled={
                        accountsLoading || walletsLoading || exchangersLoading
                      }
                      style={{ width: '100%' }}
                      options={destinationOptions}
                    />
                  </Form.Item>
                )}
              />
            </Col>
          ) : accountsLoading || walletsLoading || exchangersLoading ? (
            <Col span={8} className="d-flex align-items-center">
              <Spin />
            </Col>
          ) : (
            (Boolean(accountsError) ||
              Boolean(walletsError) ||
              Boolean(exchangersError)) && (
              <Col span={6} className="d-flex align-items-end">
                <Alert
                  type="error"
                  message={`У данного клиента нет данных по ${destinationType}`}
                />
              </Col>
            )
          )}
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={8}>
            <Controller
              name="gatewayId"
              control={control}
              rules={{
                required: 'Gateway обязателен',
              }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Gateway"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                >
                  <Select
                    {...field}
                    placeholder="Choose gateway"
                    showSearch
                    filterOption={false}
                    loading={isGatewaysLoading}
                    style={{ width: '100%' }}
                    options={gateways?.map((item) => ({
                      label: `${item.gatewayId} ${item.internalName}`,
                      value: item.gatewayId,
                    }))}
                    onSearch={(text) => {
                      debounceGatewaysSearch.current(text)
                    }}
                    onChange={(value) => {
                      field.onChange(value)
                      debounceGatewaysSearch.current('')
                    }}
                  />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={8}>
            <Controller
              name="initialAmount"
              control={control}
              rules={{
                validate: (value) => {
                  if (value === '0' && !clientAmount) {
                    return 'Значение не должно быть равно нулю'
                  }
                  if (value && clientAmount) {
                    return 'Значения client amount и initial amount не должны быть заполнены одновременно'
                  }
                  return true
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Initial Amount"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                >
                  <Input {...field} />
                </Form.Item>
              )}
            />
          </Col>
          <Col span={8}>
            <Controller
              name="clientAmount"
              control={control}
              rules={{
                validate: (value) => {
                  if (value === '0' && !initialAmount) {
                    return 'Значение не должно быть равно нулю'
                  }
                  if (value && initialAmount) {
                    return 'Знаения client amount и initial amount не должны быть заполнены одновременно'
                  }
                  return true
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Client Amount"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                >
                  <Input {...field} />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={16}>
            <Controller
              name="comment"
              control={control}
              render={({ field }) => (
                <Form.Item label="Comment">
                  <Input.TextArea {...field} />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={16}>
            <Controller
              name="internalComment"
              control={control}
              rules={{ required: 'Internal comment обязателен' }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Internal comment"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                >
                  <Input.TextArea {...field} />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={16} className="d-flex justify-content-end">
            <Button
              type="primary"
              htmlType="submit"
              disabled={!isDisplayDestinationId || isCreateDepositLoading}
              loading={isCreateDepositLoading}
            >
              Create
            </Button>
          </Col>
        </Row>
      </form>
    </div>
  )
}
